import { useMemo, type ReactNode } from 'react'
import merge from 'lodash/merge'
import { CssBaseline } from '@mui/material'
import {
    ThemeProvider as MuiThemeProvider,
    createTheme,
    useTheme,
} from '@mui/material/styles'
import palette from '../../theme/palette'
import shadows from '../../theme/shadows'
import customShadows from '../../theme/customShadows'
import componentsOverride from '../../theme/overrides'
import {
    buildPrimaryPaletteFromHex,
    buildPrimaryShadow,
    normalizeMenuColorPrincipal,
    normalizeMenuTemaModo,
} from './menuTheme'
import { useMenuBranding } from './MenuBrandingContext'

type Props = {
    children: ReactNode
}

export function MenuThemeProvider({ children }: Props) {
    const outerTheme = useTheme()
    const { temaModo, colorPrincipal } = useMenuBranding()

    const theme = useMemo(() => {
        const mode = normalizeMenuTemaModo(temaModo)
        const primary = buildPrimaryPaletteFromHex(
            normalizeMenuColorPrincipal(colorPrincipal)
        )
        const modePalette = palette(mode)
        const modeCustomShadows = customShadows(mode)

        const nextTheme = createTheme(
            merge({}, outerTheme, {
                palette: {
                    ...modePalette,
                    primary,
                },
                shadows: shadows(mode),
                customShadows: {
                    ...modeCustomShadows,
                    primary: buildPrimaryShadow(primary.main),
                },
            })
        )

        // Regenerar overrides con la paleta del menú (modo claro/oscuro + color principal).
        // Si se reutilizan los components del tema global, el focus de inputs queda
        // con colores del modo incorrecto (p. ej. label blanco sobre fondo claro).
        const overrides = componentsOverride(nextTheme)
        const buttonOverride = overrides.MuiButton ?? {}

        nextTheme.components = {
            ...overrides,
            MuiButton: {
                ...buttonOverride,
                variants: [
                    ...(buttonOverride.variants ?? []),
                    {
                        props: {
                            variant: 'contained',
                            color: 'primary',
                        },
                        style: {
                            '&:hover': {
                                backgroundColor: primary.dark,
                                boxShadow: buildPrimaryShadow(primary.main),
                            },
                        },
                    },
                ],
            },
        }

        return nextTheme
    }, [colorPrincipal, outerTheme, temaModo])

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            {children}
        </MuiThemeProvider>
    )
}
